import { useEffect, useState } from "react";
import {
  Container, Title, Text, Card, Group, SimpleGrid, Table, Center, Loader, Anchor,
} from "@mantine/core";
import { IconReceipt2 } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import { usePlan } from "../hooks/usePlan";
import { getCreditLedger } from "./pricingApi";
import ResponsiveTable from "../components/ResponsiveTable";

// Ledger action codes → localized labels (falls back to the raw code for anything new).
const ACTION_KEY = {
  linkedInFetch: "spend_action_linkedinFetch",
  webSearch: "spend_action_webSearch",
  cvExtraction: "spend_action_cvExtraction",
  compensationExtraction: "spend_action_compensationExtraction",
  consentParse: "spend_action_consentParse",
  documentClassification: "spend_action_documentClassification",
  transcriptExtraction: "spend_action_transcriptExtraction",
  publicDataExtraction: "spend_action_publicDataExtraction",
  searchProfile: "spend_action_searchProfile",
  searchEmbedding: "spend_action_searchEmbedding",
  rhpDraft: "spend_action_rhpDraft",
  transcriptAnalysis: "spend_action_transcriptAnalysis",
  statusReasons: "spend_action_statusReasons",
  other: "spend_action_other",
};
const actionLabel = (a, t) => t(ACTION_KEY[a] || "", { defaultValue: a });

/**
 * Read-only spend report over the credit ledger. The headline + by-action breakdown cover the
 * LAST 30 DAYS — the figure that describes what using the system actually costs; a lifetime sum
 * is dominated by one-time events (the initial mass import) and misreads as a running cost, so
 * it is deliberately not shown. An audit view, NOT a billing source of truth: ledger writes are
 * best-effort and the current balance reflects active credit batches, so spend and balance are
 * shown as two distinct facts (never reconciled). Also the after-the-fact record that explains a
 * thin enrichment (an under-funded run bills only the phases it could afford).
 */
export default function SpendReportPage() {
  const { t, i18n } = useTranslation("Veft");
  const { creditsBalance } = usePlan();
  const [, setLocation] = useLocation();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getCreditLedger()
      .then(setReport)
      .catch(() => setReport({ entries: [], byAction: [], windowDays: 30, windowCredits: 0, totalCount: 0, entryCap: 500, eurPer1000Credits: 3 }))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Center py="xl"><Loader /></Center>;

  const per1000 = report?.eurPer1000Credits ?? 3;
  const eur = (credits) => `€${((credits / 1000) * per1000).toFixed(2)}`;
  const num = (n) => (n ?? 0).toLocaleString(i18n.language);
  const fmtDate = (d) => new Date(d).toLocaleDateString(i18n.language, { day: "2-digit", month: "2-digit", year: "numeric" });
  const windowDays = report.windowDays ?? 30;

  return (
    <Container size="md" py="md">
      <Title order={3} mb="xs">
        <Group gap="xs"><IconReceipt2 size={22} /> {t("spend_report_title")}</Group>
      </Title>
      <Text size="sm" c="dimmed" mb="md">{t("spend_report_subtitle", { n: windowDays })}</Text>

      <SimpleGrid cols={{ base: 1, sm: 2 }} mb="md">
        <Card>
          <Text size="xs" c="dimmed">{t("spend_last_window", { n: windowDays })}</Text>
          <Text fw={700} size="xl">{num(report.windowCredits)} <Text span size="sm" c="dimmed">{t("spend_credits_unit")}</Text></Text>
          <Text size="sm" c="dimmed">≈ {eur(report.windowCredits)}</Text>
        </Card>
        <Card>
          <Text size="xs" c="dimmed">{t("spend_balance")}</Text>
          <Text fw={700} size="xl">{num(creditsBalance)} <Text span size="sm" c="dimmed">{t("spend_credits_unit")}</Text></Text>
        </Card>
      </SimpleGrid>

      {report.byAction.length > 0 && (
        <Card mb="md">
          <Title order={5} mb="sm">{t("spend_by_action", { n: windowDays })}</Title>
          <ResponsiveTable minWidth={440}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("spend_col_action")}</Table.Th>
                <Table.Th ta="right">{t("spend_col_count")}</Table.Th>
                <Table.Th ta="right">{t("spend_col_credits")}</Table.Th>
                <Table.Th ta="right">{t("spend_col_cost")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {report.byAction.map((a) => (
                <Table.Tr key={a.action}>
                  <Table.Td>{actionLabel(a.action, t)}</Table.Td>
                  <Table.Td ta="right">{num(a.count)}</Table.Td>
                  <Table.Td ta="right">{num(a.credits)}</Table.Td>
                  <Table.Td ta="right">{eur(a.credits)}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </ResponsiveTable>
        </Card>
      )}

      <Card>
        <Title order={5} mb="sm">{t("spend_recent")}</Title>
        {report.entries.length === 0 ? (
          <Text size="sm" c="dimmed">{t("spend_empty")}</Text>
        ) : (
          <ResponsiveTable minWidth={520}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("spend_col_date")}</Table.Th>
                <Table.Th>{t("spend_col_action")}</Table.Th>
                <Table.Th>{t("spend_col_detail")}</Table.Th>
                <Table.Th ta="right">{t("spend_col_credits")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {report.entries.map((e, idx) => (
                <Table.Tr key={idx}>
                  <Table.Td>{fmtDate(e.at)}</Table.Td>
                  <Table.Td>{actionLabel(e.action, t)}</Table.Td>
                  <Table.Td>
                    {e.candidateId ? (
                      <Anchor size="sm" href={`/candidates/${e.candidateId}`}
                        onClick={(ev) => { ev.preventDefault(); setLocation(`/candidates/${e.candidateId}`); }}>
                        {e.description || "—"}
                      </Anchor>
                    ) : (e.description || "—")}
                  </Table.Td>
                  <Table.Td ta="right">{num(e.credits)}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </ResponsiveTable>
        )}
        {report.totalCount >= report.entryCap && (
          <Text size="xs" c="dimmed" mt="sm">{t("spend_capped", { n: report.entryCap })}</Text>
        )}
        <Text size="xs" c="dimmed" mt="sm">{t("spend_history_note")}</Text>
      </Card>
    </Container>
  );
}
